import { Chip, IconButton, makeStyles, SvgIcon, TableCell, Tooltip } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import RepeatIcon from '@material-ui/icons/Repeat';
import ShoppingBasketIcon from '@material-ui/icons/ShoppingBasket';
import { useTranslation } from 'react-i18next';
import { ReactComponent as GarageMoneyIcon } from 'src/assets/icons/muiV5/garage_money_2.svg';
import useApi from 'src/hooks/useApi';
import useRole from 'src/hooks/useRole';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import ApiService from 'src/modules/api-service';
import { formatUnitValue } from 'src/modules/unit-helpers';
import UserRole from 'src/types/UserRoles';

import { BID_DATE_TIME_FORMAT, formatDate } from '../modules/date-helpers';
import {
  BidAllocationRequestDTO,
  BidAutomationType,
  BidSuccessStatus,
  BidType,
  IndividualBidDTO,
  MonetaryAmount,
  SourceType,
  UnitEntry,
} from '../modules/generated/api';

import BidSuccessStatusChip from './BidSuccessStatusChip';
import InfoTooltip from './InfoTooltip';
import UnitValue from './UnitValue';

type BidTableCellsProps = {
  bid: IndividualBidDTO;
  source?: SourceType;
  refresh: () => void;
  dateEnd?: string;
  bidId?: string;
};

const useStyles = makeStyles((theme) => ({
  green: {
    backgroundColor: green[300],
  },
  statusChip: {
    color: theme.palette.common.white,
    marginBottom: theme.spacing(0.5),
    '&--primary': {
      backgroundColor: theme.palette.primary.main,
    },
    '&--secondary': {
      backgroundColor: theme.palette.secondary.main,
    },
    '&--error': {
      backgroundColor: theme.palette.error.main,
    },
    '&--success': {
      backgroundColor: theme.palette.success.main,
    },
  },
  statusChipIcon: {
    color: theme.palette.common.white,
  },
  buyButton: {
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
  },
  cellAlign: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  iconCell: {
    paddingRight: 0,
    paddingLeft: 0,
  },
  valueWithIcon: {
    marginBottom: '5px',
  },
  bidValueContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  strikethrough: {
    textDecoration: 'line-through',
    opacity: 0.7,
  },
  allocatedBidContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
  },
  smallerCell: {
    width: '50px',
  },
  noRightPadding: {
    paddingRight: 0,
  },
  intelligenceIcon: {
    color: theme.palette.intelligence.main,
  },
}));

const BidTableCells = ({ bid, source, refresh, dateEnd, bidId }: BidTableCellsProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { hasRole } = useRole();
  const isAdmin = hasRole(UserRole.ADMIN);
  const snackbar = useCustomSnackbarGlobal();
  const { fetch } = useApi();

  const auctionHasEnded = dateEnd && new Date(dateEnd) < new Date();
  const originalBidFormatted = () => {
    if (bid.originalBid && bid.originalBid.value && bid.originalBid.value > 0) {
      return formatUnitValue(bid.originalBid.value, bid.originalBid.unit);
    }

    return t('common.unavailable');
  };
  const handleSetToBought = async (dealerId?: string, unitEntry?: UnitEntry) => {
    if (!bidId || !source) return;
    const monetaryAmount: MonetaryAmount = {
      amount: unitEntry?.value || 0,
      currency: unitEntry?.unit || '',
    };
    const requestDto: BidAllocationRequestDTO = {
      dealerId: dealerId || '',
      sellingPrice: monetaryAmount,
    };
    const response = await fetch(ApiService.bids.bidControllerSetBidStatusToBought(source, bidId, requestDto));
    if (response && response.status === 200) {
      snackbar.showSuccess(t('snackBar.success.stateToBuy'));
      refresh();
    } else {
      snackbar.showError(t('snackBar.error.stateToBuy'));
    }
  };

  return (
    <>
      <TableCell className={classes.noRightPadding}>{`${bid.user?.name} (${bid.dealer?.id})`}</TableCell>
      <TableCell className={classes.iconCell}>
        <span className={classes.cellAlign}>
          {bid.automatedBy === BidAutomationType.Repeat && (
            <Tooltip placement="left" title={t('bids.bidWasSentAutomatically')}>
              <AutorenewIcon color="primary" fontSize="small" />
            </Tooltip>
          )}
          {bid.automatedBy !== BidAutomationType.Repeat &&
            bid.autoRepeatBidOnSubsequentOffer && ( // if the bid was placed automatically we do not show that the replacement is active
              <Tooltip placement="left" title={t('bids.bidAutomationStarted')}>
                <RepeatIcon color="secondary" fontSize="small" />
              </Tooltip>
            )}
          {bid.automatedBy === BidAutomationType.PurchaseAgent && (
            <Tooltip placement="left" title={t('bids.bidPlacedByPurchaseAgent')}>
              <SvgIcon
                component={GarageMoneyIcon as any}
                viewBox="0 -960 960 960"
                fontSize="small"
                className={classes.intelligenceIcon}
              />
            </Tooltip>
          )}
        </span>
      </TableCell>
      <TableCell>{bid.kvps}</TableCell>
      <TableCell className={classes.smallerCell}>
        <span className={classes.bidValueContainer}>
          {bid.originalBid && (
            <InfoTooltip
              title={t('bids.originalBidReplacedByForeignBid.title', {
                originalBid: originalBidFormatted(),
              })}
              hint={t('bids.originalBidReplacedByForeignBid.hint')}
              placement="left"
            />
          )}
          {bid.bidType === BidType.Auction && bid.allocatedBid && bid.allocatedBid?.amount !== bid.bid?.value ? (
            <>
              <InfoTooltip
                title={t('bids.originalBidPartOfAuction.title', {
                  originalBid: formatUnitValue(bid.bid?.value, bid.bid?.unit),
                })}
                hint={t('bids.originalBidPartOfAuction.hint')}
                placement="left"
              />
              <span className={classes.allocatedBidContainer}>
                <span className={classes.strikethrough}>
                  <UnitValue alt={t('common.unavailable')} value={bid.bid?.value} unit={bid.bid?.unit} />
                </span>
                <UnitValue alt={t('common.unavailable')} value={bid.allocatedBid?.amount} unit={bid.bid?.unit} />
              </span>
            </>
          ) : (
            <UnitValue alt={t('common.unavailable')} value={bid.bid?.value} unit={bid.bid?.unit} />
          )}
        </span>
      </TableCell>
      <TableCell>{formatDate(bid.sentTime || '', BID_DATE_TIME_FORMAT)}</TableCell>
      {bid.status && (
        <TableCell>
          <BidSuccessStatusChip errorMessage={bid.errorMessage} successStatus={bid.status} />
          {(bid.status === BidSuccessStatus.Allocate || bid.status === BidSuccessStatus.Sent) &&
            auctionHasEnded &&
            isAdmin &&
            source === SourceType.SpotdealsAllmobil && (
              <Tooltip title={t('bids.setToBought')} arrow placement="left">
                <IconButton
                  size="small"
                  className={classes.buyButton}
                  onClick={() => handleSetToBought(bid.dealer?.id, bid.bid)}
                >
                  <ShoppingBasketIcon />
                </IconButton>
              </Tooltip>
            )}
        </TableCell>
      )}
      {!bid.status && !bid.sentTime && (
        <TableCell>
          <Chip label={t('common.pending')} className={classes.statusChip} size="small" color="secondary" />
        </TableCell>
      )}
    </>
  );
};

export default BidTableCells;
